import {
  Button,
  Group,
  Modal,
  NumberInput,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { useState } from "react";
import { useCampusOptions } from "../groups/group-queries";
import { useSavePlan, type PlanFields } from "./services-queries";

export interface PlanModalProps {
  readonly opened: boolean;
  readonly seasonId: string;
  /** A default start for the new plan, as a datetime-local value. */
  readonly defaultStartsAt?: string;
  readonly onClose: () => void;
}

/** A plan is one service on a date (ADR 0043 §2). Dates are added one at a time; a season is a handful. */
export function PlanModal({ opened, seasonId, defaultStartsAt, onClose }: PlanModalProps) {
  const save = useSavePlan();
  const campuses = useCampusOptions();
  const [fields, setFields] = useState<PlanFields>({
    title: "Sunday 9:00",
    startsAt: defaultStartsAt ?? "",
    minutes: 90,
    campusId: null,
    notes: "",
  });
  const code = (save.error as { code?: string } | null)?.code;
  const errorText =
    code === "23514"
      ? "A plan needs a title, a start and an end after it."
      : code === "P0002"
        ? "The campus is not one of this church's. Choose again."
        : save.isError
          ? "The plan was not saved. Check your access and try again."
          : null;
  return (
    <Modal opened={opened} onClose={onClose} title="Add a plan" radius="lg">
      <Stack gap="sm">
        <TextInput
          label="Title"
          required
          value={fields.title}
          onChange={(e) => {
            const title = e.currentTarget.value;
            setFields((c) => ({ ...c, title }));
          }}
        />
        <Group grow align="flex-start">
          <TextInput
            label="Starts"
            type="datetime-local"
            required
            value={fields.startsAt}
            onChange={(e) => {
              const startsAt = e.currentTarget.value;
              setFields((c) => ({ ...c, startsAt }));
            }}
          />
          <NumberInput
            label="Minutes"
            min={15}
            max={720}
            value={fields.minutes}
            onChange={(value) => {
              setFields((c) => ({ ...c, minutes: typeof value === "number" ? value : 90 }));
            }}
          />
        </Group>
        <Select
          label="Campus"
          placeholder="None"
          clearable
          data={campuses.data ?? []}
          value={fields.campusId}
          onChange={(value) => {
            setFields((c) => ({ ...c, campusId: value }));
          }}
        />
        <Textarea
          label="Notes"
          placeholder="Anything the team should know about this date."
          autosize
          minRows={2}
          value={fields.notes}
          onChange={(e) => {
            const notes = e.currentTarget.value;
            setFields((c) => ({ ...c, notes }));
          }}
        />
        {errorText !== null ? (
          <Text size="sm" c="red">
            {errorText}
          </Text>
        ) : null}
        <Group justify="flex-end" gap="sm" mt="xs">
          <Button variant="subtle" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={fields.title.trim() === "" || fields.startsAt === ""}
            loading={save.isPending}
            onClick={() => {
              save.mutate({ planId: null, seasonId, fields }, { onSuccess: onClose });
            }}
          >
            Add plan
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
